import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import {
  updateProfile,
  deleteAccount,
  exportUserData,
} from '../lib/functions';
import type { ExportUserDataResponse } from '../lib/functions';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import type { Profile } from '../types/user';
import type { BusinessPlan } from '../types/businessPlan';
import type { TokenLedgerEntry } from '../types/tokens';
import { STAGE_ORDER, STAGE_CONFIGS } from '../data/stageConfigs';
import { signOut } from '../hooks/useAuth';

type Tab = 'activity' | 'ai' | 'controls' | 'account';

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function relativeTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(date);
}


export default function ParentPortalPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [childProfiles, setChildProfiles] = useState<Profile[]>([]);
  const [selectedChild, setSelectedChild] = useState<Profile | null>(null);
  const [childPlans, setChildPlans] = useState<BusinessPlan[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<TokenLedgerEntry[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('activity');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(
          collection(db, COLLECTIONS.USERS, user.uid, SUBCOLLECTIONS.PROFILES),
        );
        const profiles = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Profile);
        if (profiles.length === 0) { navigate('/launchpad/latest'); return; }
        setChildProfiles(profiles);
        setSelectedChild(profiles[0]);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user, navigate]);

  // Real-time data for selected child
  useEffect(() => {
    if (!user || !selectedChild) return;
    const unsubs: (() => void)[] = [];

    // Plans
    const plansRef = collection(db, COLLECTIONS.USERS, user.uid, SUBCOLLECTIONS.PROFILES, selectedChild.id, SUBCOLLECTIONS.BUSINESS_PLANS);
    unsubs.push(onSnapshot(query(plansRef, orderBy('createdAt', 'desc')), (snap) => {
      setChildPlans(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as BusinessPlan));
    }));

    // Ledger
    const ledgerRef = collection(db, COLLECTIONS.USERS, user.uid, SUBCOLLECTIONS.PROFILES, selectedChild.id, SUBCOLLECTIONS.TOKEN_LEDGER);
    unsubs.push(onSnapshot(query(ledgerRef, orderBy('timestamp', 'desc'), limit(30)), (snap) => {
      setLedgerEntries(snap.docs.map((d) => ({ ...d.data() }) as TokenLedgerEntry));
    }));

    return () => unsubs.forEach((u) => u());
  }, [user, selectedChild]);

  if (loading) return <LoadingScreen />;

  const tabs: { key: Tab; label: string; emoji: string }[] = [
    { key: 'activity', label: 'Activity', emoji: '📊' },
    { key: 'ai', label: 'AI Usage', emoji: '🤖' },
    { key: 'controls', label: 'Controls', emoji: '⚙️' },
    { key: 'account', label: 'Account', emoji: '👤' },
  ];

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-4 pb-12 pt-8">
        <div className="mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Parent Portal</h1>
            <p className="mt-1 text-gray-500">Monitor and manage your child&apos;s activity</p>
          </motion.div>

          {childProfiles.length > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              {childProfiles.map((c) => (
                <button key={c.id} onClick={() => setSelectedChild(c)} className={`min-h-[44px] rounded-xl px-4 py-2 text-sm font-medium transition-all ${selectedChild?.id === c.id ? 'bg-brand-500 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                  {c.explorerName}
                </button>
              ))}
            </div>
          )}

          {selectedChild && (
            <div className="mt-8 md:flex md:gap-6">
              <div className="mb-4 flex gap-2 overflow-x-auto md:mb-0 md:w-48 md:flex-col">
                {tabs.map((tab) => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all md:w-full ${activeTab === tab.key ? 'bg-brand-500 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                    <span>{tab.emoji}</span><span>{tab.label}</span>
                  </button>
                ))}
              </div>

              <div className="flex-1">
                <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border-2 border-gray-100 bg-white p-6 shadow-sm">
                  {activeTab === 'activity' && <ActivityPanel child={selectedChild} plans={childPlans} ledger={ledgerEntries} uid={user?.uid ?? ''} />}
                  {activeTab === 'ai' && <AIUsagePanel child={selectedChild} ledger={ledgerEntries} uid={user?.uid ?? ''} showToast={showToast} />}
                  {activeTab === 'controls' && <ControlsPanel child={selectedChild} uid={user?.uid ?? ''} showToast={showToast} />}
                  {activeTab === 'account' && <AccountPanel child={selectedChild} email={user?.email ?? ''} uid={user?.uid ?? ''} showToast={showToast} />}
                </motion.div>
              </div>
            </div>
          )}
        </div>

        {toastMessage && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </motion.div>
        )}
      </div>
    </AnimatedPage>
  );
}

/* ──────────────── Activity Timeline Panel ──────────────── */

interface TimelineEvent {
  date: Date;
  icon: string;
  title: string;
  detail: string;
}

function ActivityPanel({ child, plans, ledger, uid }: { child: Profile; plans: BusinessPlan[]; ledger: TokenLedgerEntry[]; uid: string }) {
  const [kokoSessionCount, setKokoSessionCount] = useState(0);

  useEffect(() => {
    if (!uid || !child.id) return;
    const loadKoko = async () => {
      try {
        const q = query(
          collection(db, COLLECTIONS.KOKO_INTERACTIONS),
          where('userId', '==', uid),
          where('profileId', '==', child.id),
          where('platform', '==', 'web'),
          orderBy('createdAt', 'desc'),
          limit(20),
        );
        const snap = await getDocs(q);
        setKokoSessionCount(snap.size);
      } catch { /* index may not exist */ }
    };
    loadKoko();
  }, [uid, child.id]);

  // Build timeline events
  const events: TimelineEvent[] = [];

  plans.forEach((plan) => {
    STAGE_ORDER.forEach((key) => {
      const progress = plan.stageProgress?.[key];
      if (progress?.isCompleted && progress.completedAt?.toDate) {
        const config = STAGE_CONFIGS[key];
        events.push({
          date: progress.completedAt.toDate(),
          icon: config.emoji,
          title: `Completed ${config.name}`,
          detail: `${plan.businessName || 'Plan'} • ${formatTime(progress.timeSpent)}`,
        });
      }
    });
  });

  ledger.slice(0, 15).forEach((entry) => {
    if (entry.timestamp?.toDate) {
      events.push({
        date: entry.timestamp.toDate(),
        icon: entry.amount > 0 ? '🪙' : '💸',
        title: entry.description,
        detail: `${entry.amount > 0 ? '+' : ''}${entry.amount} tokens`,
      });
    }
  });

  if (kokoSessionCount > 0) {
    events.push({
      date: new Date(),
      icon: '🤖',
      title: `${kokoSessionCount} KOKO chat sessions`,
      detail: 'AI Sidekick conversations',
    });
  }

  events.sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Activity for {child.explorerName}</h2>
        <p className="text-sm text-gray-500">{plans.length} business plan{plans.length !== 1 ? 's' : ''}</p>
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-gray-400">No activity yet.</p>
      ) : (
        <div className="space-y-2">
          {events.slice(0, 25).map((ev, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }} className="flex items-start gap-3 rounded-xl bg-gray-50 p-3">
              <span className="mt-0.5 text-lg">{ev.icon}</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{ev.title}</p>
                <p className="text-xs text-gray-400">{ev.detail}</p>
              </div>
              <span className="shrink-0 text-xs text-gray-400">{relativeTime(ev.date)}</span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────── AI Usage Panel ──────────────── */

function AIUsagePanel({ child, ledger, uid, showToast }: { child: Profile; ledger: TokenLedgerEntry[]; uid: string; showToast: (m: string) => void }) {
  const [sessionCount, setSessionCount] = useState(0);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const consentGranted = child.aiConsent?.granted ?? false;

  useEffect(() => {
    if (!uid || !child.id) return;
    const load = async () => {
      try {
        const q = query(
          collection(db, COLLECTIONS.KOKO_INTERACTIONS),
          where('userId', '==', uid),
          where('profileId', '==', child.id),
          orderBy('createdAt', 'desc'),
          limit(50),
        );
        const snap = await getDocs(q);
        setSessionCount(snap.size);
      } catch { /* ignore */ }
    };
    load();
  }, [uid, child.id]);

  const aiLedger = ledger.filter((e) =>
    e.feature?.includes('koko') || e.feature?.includes('ai') || e.feature?.includes('sidekick'),
  );
  const aiTokensSpent = aiLedger.reduce((sum, e) => sum + Math.abs(Math.min(0, e.amount)), 0);

  // Simple 7-day usage bars
  const dailyUsage: number[] = [];
  for (let i = 6; i >= 0; i--) {
    const dayStart = new Date();
    dayStart.setDate(dayStart.getDate() - i);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const count = aiLedger.filter((e) => {
      const t = e.timestamp?.toDate?.();
      return t && t >= dayStart && t < dayEnd;
    }).length;
    dailyUsage.push(count);
  }
  const maxUsage = Math.max(...dailyUsage, 1);
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const today = new Date().getDay();
  const orderedLabels = Array.from({ length: 7 }, (_, i) => dayLabels[(today - 6 + i + 7) % 7]);

  const handleToggleAI = async () => {
    if (consentGranted) {
      setShowRevokeConfirm(true);
      return;
    }
    try {
      await updateProfile({ profileId: child.id, fields: { 'consentStatus.ai': true, 'aiConsent.granted': true, 'aiConsent.grantedAt': new Date() } });
      showToast(`AI consent granted for ${child.explorerName}`);
    } catch {
      showToast('Failed to update. Please try again.');
    }
  };

  const confirmRevoke = async () => {
    try {
      await updateProfile({ profileId: child.id, fields: { 'consentStatus.ai': false, 'aiConsent.granted': false, 'aiConsent.revokedAt': new Date() } });
      showToast(`AI consent revoked for ${child.explorerName}`);
    } catch {
      showToast('Failed to update. Please try again.');
    }
    setShowRevokeConfirm(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">AI Usage</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-blue-50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{sessionCount}</p>
          <p className="text-xs text-blue-500">Total Sessions</p>
        </div>
        <div className="rounded-xl bg-purple-50 p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">{aiTokensSpent}</p>
          <p className="text-xs text-purple-500">Tokens on AI</p>
        </div>
      </div>

      {/* 7-day chart */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="mb-3 text-sm font-medium text-gray-700">Last 7 days</p>
        <div className="flex items-end gap-2">
          {dailyUsage.map((count, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1">
              <div className="w-full rounded-t bg-brand-400" style={{ height: `${Math.max(4, (count / maxUsage) * 60)}px` }} />
              <span className="text-[9px] text-gray-400">{orderedLabels[i]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Consent */}
      <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div>
          <p className="font-medium text-gray-900">AI Sidekick Access</p>
          <p className="text-sm text-gray-500">{consentGranted ? 'KOKO is active' : 'KOKO is disabled'}</p>
        </div>
        <button onClick={handleToggleAI} className={`relative inline-flex h-7 w-12 min-w-[48px] items-center rounded-full transition-colors ${consentGranted ? 'bg-green-500' : 'bg-gray-300'}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${consentGranted ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {sessionCount === 0 && (
        <div className="rounded-xl bg-blue-50 p-6 text-center">
          <span className="text-3xl">🤖</span>
          <p className="mt-2 text-sm text-blue-700">No AI interactions yet</p>
        </div>
      )}

      {/* Revoke confirmation */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Disable KOKO?</h3>
            <p className="mt-2 text-sm text-gray-600">This will disable KOKO for {child.explorerName}. They can still use the Launchpad without AI.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowRevokeConfirm(false)} className="min-h-[44px] flex-1 rounded-xl border-2 border-gray-200 py-2 font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmRevoke} className="min-h-[44px] flex-1 rounded-xl bg-red-500 py-2 font-medium text-white hover:bg-red-600">Disable</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────── Controls Panel ──────────────── */

function Toggle({ value, onChange, label, description }: { value: boolean; onChange: (v: boolean) => void; label: string; description: string }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
      <button onClick={() => onChange(!value)} className={`relative inline-flex h-7 w-12 min-w-[48px] items-center rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-gray-300'}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function ControlsPanel({ child, showToast }: { child: Profile; uid: string; showToast: (m: string) => void }) {
  const [privacy, setPrivacy] = useState({
    discoveryLevel: child.privacySettings?.discoveryLevel ?? 'public',
    allowDirectMessages: child.privacySettings?.allowDirectMessages ?? false,
    showInSearch: child.privacySettings?.showInSearch ?? true,
    allowStorySharing: child.privacySettings?.allowStorySharing ?? true,
  });
  const [parental, setParental] = useState({
    maxFriends: child.parentalSettings?.maxFriends ?? 50,
    requireParentApproval: child.parentalSettings?.requireParentApproval ?? true,
    contentFilteringLevel: child.parentalSettings?.contentFilteringLevel ?? 'standard',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        profileId: child.id,
        fields: {
          privacySettings: privacy,
          parentalSettings: parental,
        },
      });
      showToast('Settings saved!');
    } catch {
      showToast('Failed to save settings.');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">Controls</h2>

      {/* Privacy */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="mb-3 font-medium text-gray-900">Privacy Settings</p>
        <div className="space-y-1">
          <div className="py-2">
            <label className="text-sm font-medium text-gray-800">Discovery Level</label>
            <select value={privacy.discoveryLevel} onChange={(e) => setPrivacy((p) => ({ ...p, discoveryLevel: e.target.value }))} className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="public">Public</option>
              <option value="friends-only">Friends Only</option>
              <option value="private">Private</option>
            </select>
          </div>
          <Toggle value={privacy.allowDirectMessages} onChange={(v) => setPrivacy((p) => ({ ...p, allowDirectMessages: v }))} label="Allow Direct Messages" description="Let other explorers message this profile" />
          <Toggle value={privacy.showInSearch} onChange={(v) => setPrivacy((p) => ({ ...p, showInSearch: v }))} label="Show in Search" description="Appear in explorer search results" />
          <Toggle value={privacy.allowStorySharing} onChange={(v) => setPrivacy((p) => ({ ...p, allowStorySharing: v }))} label="Allow Story Sharing" description="Let others see shared stories" />
        </div>
      </div>

      {/* Parental */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="mb-3 font-medium text-gray-900">Parental Settings</p>
        <div className="space-y-1">
          <div className="py-2">
            <label className="text-sm font-medium text-gray-800">Max Friends</label>
            <input type="number" min={0} max={200} value={parental.maxFriends} onChange={(e) => setParental((p) => ({ ...p, maxFriends: parseInt(e.target.value) || 0 }))} className="mt-1 block w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <Toggle value={parental.requireParentApproval} onChange={(v) => setParental((p) => ({ ...p, requireParentApproval: v }))} label="Require Parent Approval" description="Approve friend requests before connecting" />
          <div className="py-2">
            <label className="text-sm font-medium text-gray-800">Content Filtering</label>
            <select value={parental.contentFilteringLevel} onChange={(e) => setParental((p) => ({ ...p, contentFilteringLevel: e.target.value }))} className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="standard">Standard</option>
              <option value="strict">Strict</option>
            </select>
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="min-h-[44px] w-full rounded-xl bg-brand-500 py-3 font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50">
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
}

/* ──────────────── Account Panel ──────────────── */

function AccountPanel({ child, email, showToast }: { child: Profile; email: string; uid: string; showToast: (m: string) => void }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const createdAt = child.createdAt?.toDate?.() ? formatDate(child.createdAt.toDate()) : 'Unknown';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount({ profileId: child.id });
      showToast('Account deactivated. All data will be permanently deleted in 30 days.');
      setShowDeleteModal(false);
      setTimeout(() => signOut(), 2000);
    } catch {
      showToast('Failed to delete account. Please try again.');
    }
    setDeleting(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportUserData({ profileId: child.id });
      const data = res.data as ExportUserDataResponse;
      setDownloadUrl(data.downloadUrl);
      showToast('Your data export is ready!');
    } catch {
      showToast('Data export request submitted. You will receive an email.');
    }
    setExporting(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">Account</h2>

      {/* Info */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div className="space-y-2 text-sm text-gray-600">
          <p>Email: <span className="font-medium">{email}</span></p>
          <p>Signed up: <span className="font-medium">{createdAt}</span></p>
          <p>Platform: <span className="font-medium capitalize">{child.signupPlatform ?? 'web'}</span></p>
        </div>
      </div>

      {/* Access */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="font-medium text-gray-900">Access</p>
        <p className="mt-2 text-sm text-gray-600">OMore is free. {child.explorerName ?? 'Your child'} has full access.</p>
      </div>

      {/* Export */}
      <button onClick={handleExport} disabled={exporting} className="min-h-[44px] w-full rounded-xl border-2 border-gray-200 py-3 font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
        {exporting ? 'Generating export…' : 'Export My Data'}
      </button>
      {downloadUrl && (
        <div className="rounded-xl bg-green-50 p-4 text-center">
          <p className="text-sm text-green-700">Your data export is ready.</p>
          <a href={downloadUrl} className="mt-1 inline-block text-sm font-medium text-green-600 underline" target="_blank" rel="noopener noreferrer">Download (expires 24h)</a>
        </div>
      )}

      {/* Delete */}
      <button onClick={() => setShowDeleteModal(true)} className="min-h-[44px] w-full rounded-xl border-2 border-red-200 py-3 font-medium text-red-600 hover:bg-red-50">Delete Account</button>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-red-600">Delete {child.explorerName}&apos;s Account?</h3>
            <p className="mt-2 text-sm text-gray-600">This is irreversible after 30 days. Type <span className="font-bold">{child.explorerName}</span> to confirm.</p>
            <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder={child.explorerName} className="mt-3 w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none" />
            <div className="mt-4 flex gap-3">
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="min-h-[44px] flex-1 rounded-xl border-2 border-gray-200 py-2 font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleteConfirmText !== child.explorerName || deleting} className="min-h-[44px] flex-1 rounded-xl bg-red-500 py-2 font-medium text-white hover:bg-red-600 disabled:opacity-40">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
